import type { OverlaySlide } from '~/ui-rocco/pages/detail-shared';

/**
 * Every photograph in a project's gallery, for the image viewer.
 *
 * The gallery is page-builder modules rather than a flat list, so the images
 * have to be walked out of them: each module holds columns, and a column that
 * carries a picture has a `libraryImage` with a set of named crops. Nothing in
 * the loader gives this any more directly — `BannerImages` is the banner
 * segments (usually one) and `Thumbnail` is the browse-grid crop.
 *
 * Deduplicated on the underlying file rather than the URL: the same photograph
 * appears several times over with different crop tokens appended, and the
 * viewer must not page through the same picture four times.
 */

/** The crops a module column can carry, best first. */
const CROP_KEYS = [
    'FrontendDetailPage',
    'DetailPageStandard',
    'DetailPageNatural',
] as const;

/** The stored file, before the crop token — `/images/s1/…/photo.jpg`. */
const fileKey = (src: string): string => {
    const match = /^(.*?\.(?:jpe?g|png|webp|avif|gif))(?:\/|$)/i.exec(src);
    return match?.[1] ?? src;
};

const readSrc = (value: unknown): string | null => {
    if (!value || typeof value !== 'object') return null;
    const file = (value as Record<string, unknown>).LibraryImageFile;
    if (!file || typeof file !== 'object') return null;
    for (const key of CROP_KEYS) {
        const crop = (file as Record<string, unknown>)[key];
        if (crop && typeof crop === 'object') {
            const desktop = (crop as Record<string, unknown>).Desktop;
            if (typeof desktop === 'string' && desktop) return desktop;
        }
    }
    return null;
};

const readId = (value: unknown): number | undefined => {
    if (!value || typeof value !== 'object') return undefined;
    const id = (value as Record<string, unknown>).ID;
    return typeof id === 'number' ? id : undefined;
};

/**
 * A column's photograph as this walk sees it, or null where it has none this
 * walk can use — no `LibraryImageFile`, or none of the crops it reads.
 *
 * Shared with the film walk rather than restated there. That walk counts the
 * photographs before each film so the film can be put back among them, and a
 * count that keeps a column this one passes by, or counts one picture twice
 * where this one folds the repeats together, places the film too far down the
 * sheet. One predicate, so the two cannot drift.
 */
export const readColumnImage = (
    column: unknown,
): { src: string; key: string; id: number | undefined } | null => {
    const libraryImage = (column as Record<string, unknown> | null)
        ?.libraryImage;
    const src = readSrc(libraryImage);
    if (!src) return null;
    return { src, key: fileKey(src), id: readId(libraryImage) }
};

/** The description a column gives its picture, trimmed; empty is none. */
const readCaption = (column: unknown): string | undefined => {
    if (!column || typeof column !== 'object') return undefined;
    const caption = (column as Record<string, unknown>).caption;
    return typeof caption === 'string' && caption.trim()
        ? caption.trim()
        : undefined;
};

export const collectProjectImages = (
    modules: unknown,
    alt: string,
): OverlaySlide[] => {
    if (!Array.isArray(modules)) return [];

    const seen = new Set<string>();
    const slides: OverlaySlide[] = [];

    for (const module of modules) {
        const columns = (module as Record<string, unknown> | null)?.columns;
        if (!Array.isArray(columns)) continue;
        for (const column of columns) {
            const image = readColumnImage(column);
            if (!image) continue;
            if (seen.has(image.key)) continue;
            seen.add(image.key);
            const caption = readCaption(column);
            slides.push({
                src: image.src,
                alt,
                id: image.id,
                ...(caption ? { caption } : {}),
            });
        }
    }

    return slides;
};
